import React, { useState } from "react";
import SheetGround from "../../components/shared/ui/SheetGround";
import { AcaIcon } from "../../components/shared/aca";
import { VELLUM as T, SANS, display, mono, body, btn, SheetHead } from "../../components/shared/folio/kit";
import {
  FP,
  FieldBlock,
  FieldRow,
  ErrorNote,
  fieldInput,
  fieldArea,
  folioFormCss,
} from "../../components/shared/folio/form";
import { API_URL } from "../../config/api";

/* Write to the desk, drawn as a sheet.

   The two tracks the rest of the site reads in: the message on the measure,
   the desk's own particulars annotated in the margin. The form is the same
   title block the auth dialogs use, so filling anything in on this site is
   the same act twice. */

const P = "ct";

const INQUIRY_TYPES: { value: string; label: string }[] = [
  { value: "general",     label: "General inquiry" },
  { value: "seeker",      label: "For job seekers" },
  { value: "institution", label: "For institutions" },
  { value: "press",       label: "Press & media" },
  { value: "bug",         label: "Bug report" },
  { value: "other",       label: "Other" },
];

const FAQ = [
  {
    q: "How do I get started?",
    a: "Create an account and complete your profile. You can start browsing the register immediately, or post a position if you're an institution.",
  },
  {
    q: "Is Acanetic free for scholars?",
    a: "Yes — the full register, weekly digest, saved searches, and applications are free for individual scholars. Always.",
  },
  {
    q: "What does it cost institutions?",
    a: "A flat £89 per listing. No tiers, no surcharges, no hidden fees. The post is read by an editor before it goes live.",
  },
  {
    q: "How quickly will I hear back?",
    a: "Support replies within 24 hours, usually faster. The institutional desk responds on the same working day.",
  },
];

const DESK: { icon: "mail" | "phone" | "pin"; label: string; value: React.ReactNode; note?: string }[] = [
  {
    icon: "mail",
    label: "Email",
    value: "support@acanetic.co.uk",
    note: "Reply within 24 hours",
  },
  {
    icon: "phone",
    label: "Phone",
    value: "+44 20 7123 4567",
    note: "Mon–Fri, 9am–6pm GMT",
  },
  {
    icon: "pin",
    label: "Address",
    value: (
      <>
        Acanetic Headquarters<br />
        Cambridge Science Park<br />
        Cambridge, CB4 0WZ
      </>
    ),
  },
];

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    type: "general",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");
    try {
      const response = await fetch(`${API_URL}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error("Failed to send");
      setSubmitStatus("success");
      setFormData({ name: "", email: "", subject: "", message: "", type: "general" });
    } catch {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const setType = (value: string) => setFormData((prev) => ({ ...prev, type: value }));

  return (
    <SheetGround className={`${P}-root`} style={{ fontFamily: SANS, color: T.ink }}>
      <div
        className={`${P}-grid`}
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "0 20px",
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 1px 296px",
          columnGap: 40,
          alignItems: "start",
        }}
      >
        <div
          className={`${P}-spine`}
          aria-hidden
          style={{ gridColumn: 2, gridRow: "1 / -1", alignSelf: "stretch", width: 1, background: T.rule }}
        />

        <section className={`${P}-measure`} style={{ gridColumn: 1, paddingTop: 44, paddingBottom: 80 }}>
          <div
            style={{
              ...mono(T),
              borderBottom: `1px solid ${T.rule}`,
              paddingBottom: 12,
              marginBottom: 30,
              display: "flex",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <span>§ Contact</span>
            <span>Reply within 24h</span>
            <span>support@acanetic.co.uk</span>
          </div>

          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
            <h1 style={{ ...display(T, "clamp(36px, 5.4vw, 64px)"), lineHeight: 0.98, letterSpacing: "-0.04em" }}>
              Get in touch with{" "}
              <span style={{ color: T.accent, borderBottom: `3px solid ${T.accent}` }}>the editors</span>.
            </h1>
            <span style={mono(T, T.accent)}>§ Write to the desk</span>
          </div>

          <p style={{ ...body(T), fontSize: 18, marginTop: 26, maxWidth: 620 }}>
            Questions about a listing, a partnership, a press request, or a bug? Pick a route below and we'll
            write back.
          </p>

          <form onSubmit={handleSubmit} style={{ marginTop: 52 }}>
            <SheetHead t={T} code="§ 01 · Message form" size="clamp(24px, 3.2vw, 32px)">
              Send us a note.
            </SheetHead>

            {/* The route is chosen before the message is written: it decides
                which desk reads it, so it belongs above the fields. */}
            <div style={{ marginBottom: 22 }}>
              <div style={{ ...mono(T), marginBottom: 9 }}>Inquiry type</div>
              <div
                role="group"
                aria-label="Inquiry type"
                className={`${P}-types`}
                style={{ display: "flex", flexWrap: "wrap", border: `1.5px solid ${T.ink}`, background: T.ground }}
              >
                {INQUIRY_TYPES.map((t, i) => {
                  const on = formData.type === t.value;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setType(t.value)}
                      aria-pressed={on}
                      className={`${FP}-role`}
                      style={{
                        position: "relative",
                        flex: "1 0 auto",
                        padding: "11px 16px",
                        background: on ? T.ink : "transparent",
                        color: on ? T.ground : T.ink,
                        border: "none",
                        borderLeft: i === 0 ? "none" : `1px solid ${on ? T.ink : T.rule}`,
                        borderRadius: 0,
                        fontFamily: SANS,
                        fontSize: 13,
                        cursor: "pointer",
                      }}
                    >
                      {on && (
                        <span
                          aria-hidden
                          style={{ position: "absolute", top: 5, right: 5, width: 5, height: 5, background: T.accent }}
                        />
                      )}
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <FieldBlock>
              <FieldRow label="Name" htmlFor="name" required first>
                <input
                  id="name"
                  name="name"
                  required
                  autoComplete="name"
                  value={formData.name}
                  onChange={handleChange}
                  style={fieldInput}
                  placeholder="Eleanor Marsh"
                />
              </FieldRow>
              <FieldRow label="Email" htmlFor="email" required>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  style={fieldInput}
                  placeholder="you@university.ac.uk"
                />
              </FieldRow>
              <FieldRow label="Subject" htmlFor="subject" required>
                <input
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  style={fieldInput}
                  placeholder="What is this about?"
                />
              </FieldRow>
              <FieldRow label="Message" htmlFor="message" required stacked>
                <textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  style={fieldArea}
                  placeholder="Tell us a little about what you need."
                />
              </FieldRow>
            </FieldBlock>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`${FP}-btn`}
              style={{ ...btn(T, "accent"), marginTop: 26 }}
            >
              {isSubmitting ? "Sending…" : "Send message"}
            </button>

            {/* Announced, because the outcome of a send is not visible
                anywhere else on the page. */}
            <div aria-live="polite" style={{ marginTop: submitStatus === "idle" ? 0 : 18 }}>
              {submitStatus === "success" && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto minmax(0,1fr)",
                    gap: 12,
                    alignItems: "start",
                    border: `1.5px solid ${T.ink}`,
                    padding: "13px 15px",
                  }}
                >
                  <span aria-hidden style={{ marginTop: 3 }}>
                    <AcaIcon name="check" size={13} stroke={T.accent} />
                  </span>
                  <span style={{ fontFamily: SANS, fontSize: 14, lineHeight: 1.45, color: T.ink }}>
                    Thank you. We will write back shortly.
                  </span>
                </div>
              )}
              {submitStatus === "error" && <ErrorNote>Something went wrong. Please try again.</ErrorNote>}
            </div>
          </form>
        </section>

        <aside className={`${P}-margin`} style={{ gridColumn: 3, paddingTop: 44, paddingBottom: 80 }}>
          <div className={`${P}-margin-inner`}>
            <div style={{ ...mono(T, T.accent), marginBottom: 14 }}>§ Get in touch</div>
            <h2 style={{ ...display(T, "24px"), borderBottom: `1.5px solid ${T.ink}`, paddingBottom: 11 }}>
              The desk.
            </h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {DESK.map((d) => (
                <li key={d.label} style={{ borderBottom: `1px solid ${T.rule}`, padding: "14px 0" }}>
                  <div style={{ ...mono(T), display: "flex", alignItems: "center", gap: 9 }}>
                    <AcaIcon name={d.icon} size={13} stroke={T.mute} />
                    {d.label}
                  </div>
                  <div style={{ fontFamily: SANS, fontSize: 15.5, color: T.ink, marginTop: 6, lineHeight: 1.45 }}>
                    {d.value}
                  </div>
                  {d.note && <div style={{ ...body(T), fontSize: 13, marginTop: 3 }}>{d.note}</div>}
                </li>
              ))}
            </ul>

            <div style={{ ...mono(T, T.accent), margin: "34px 0 14px" }}>§ FAQ</div>
            <h2 style={{ ...display(T, "24px"), borderBottom: `1.5px solid ${T.ink}`, paddingBottom: 11 }}>
              Asked often.
            </h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {FAQ.map((f) => (
                <li key={f.q} style={{ borderBottom: `1px solid ${T.rule}`, padding: "14px 0" }}>
                  <div style={{ fontFamily: SANS, fontSize: 14.5, fontWeight: 600, letterSpacing: "-0.01em", color: T.ink, marginBottom: 6 }}>
                    {f.q}
                  </div>
                  <div style={{ ...body(T), fontSize: 13.5, lineHeight: 1.55 }}>{f.a}</div>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>

      <style>{`
        ${folioFormCss}

        @media (max-width: 980px) {
          .${P}-grid { grid-template-columns: 1px minmax(0, 1fr) !important; column-gap: 0 !important; }
          .${P}-spine { grid-column: 1 !important; }
          .${P}-measure, .${P}-margin { grid-column: 2 !important; padding-left: 22px !important; }
          .${P}-margin { padding-top: 0 !important; }
          .${P}-margin-inner { border-top: 1px solid ${T.rule}; padding-top: 22px; }
        }

        @media (max-width: 560px) {
          /* Six routes will not sit on one line; let each take the width. */
          .${P}-types > button { flex: 1 0 100% !important; border-left: none !important; border-top: 1px solid ${T.rule}; }
          .${P}-types > button:first-child { border-top: none; }
        }
      `}</style>
    </SheetGround>
  );
};

export default ContactPage;
